import React, { useState, useEffect } from "react";
import { Backdrop, CircularProgress, Button } from "@mui/material";
import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AADHeader from "./components/AADHeader.js";
import { useNavigate } from "react-router-dom";
import { useFeatures } from "./hooks/usePermissions";
import { explainPlanRefusal } from "./utils/authFetch";
import { TabView, TabPanel } from "primereact/tabview";

const ESGDashboard = () => {
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false); // Close sidebar on mobile
      } else {
        setIsSidebarOpen(true); // Open sidebar on larger screens
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Which plan module each tile belongs to (coming-soon tiles keep their key so
  // an org without the module sees it dimmed rather than merely "coming soon").
  const TILE_FEATURE = {
    emissions: "dashboard.environment.scope1",
    electricity: "dashboard.environment.electricity",
    waste: "dashboard.environment.waste",
    water: "dashboard.environment.water",
    biodiversity: "dashboard.environment.biodiversity",
    "business-travel": "dashboard.environment.business-travel",
    "employee-commute": "dashboard.environment.commute",
    transport: "dashboard.environment.transport-distribution",
    diversity: "dashboard.social.diversity",
    "health-safety": "dashboard.social.health-safety",
    training: "dashboard.social.training",
    "board-composition": "dashboard.governance.board",
    "customer-metrics": "dashboard.governance.customer",
    "anti-corruption": "dashboard.governance.anti-corruption",
    "supply-governance": "dashboard.governance.supply-chain",
  };
  const { has: hasFeatureKey } = useFeatures();
  const tileAllowed = (id) => hasFeatureKey(TILE_FEATURE[id]);
  const openTile = (id, path) =>
    tileAllowed(id) ? navigate(path) : explainPlanRefusal({ error: "feature_not_enabled", required: TILE_FEATURE[id] });
  const tileStyle = (id) => (tileAllowed(id) ? undefined : { opacity: 0.45, cursor: "not-allowed", filter: "grayscale(0.6)" });

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  // Define CTA items with their normal and hover icons & route paths
  const environmentItems = [
    {
      id: "emissions",
      label: "Scope 1 Emissions",
      icon: "./assets/img/dashboards/emissions.svg",
      hoverIcon: "./assets/img/dashboards/emissions-hover.svg",
      path: "/scope1",
    },
    {
      id: "electricity",
      label: "Purchased Electricity",
      icon: "./assets/img/dashboards/electricity.svg",
      hoverIcon: "./assets/img/dashboards/electricity-hover.svg",
      path: "/scope2",
    },
    {
      id: "waste",
      label: "Waste",
      icon: "./assets/img/dashboards/waste.svg",
      hoverIcon: "./assets/img/dashboards/waste-hover.svg",
      path: "/waste-dashboard",
    },
    {
      id: "water",
      label: "Water",
      icon: "./assets/img/dashboards/water.svg",
      hoverIcon: "./assets/img/dashboards/water-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "biodiversity",
      label: "Biodiversity",
      icon: "./assets/img/dashboards/biodiversity.svg",
      hoverIcon: "./assets/img/dashboards/biodiversity-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
  ];

  const nestedItems = [
    {
      id: "business-travel",
      label: "Business Travel",
      icon: "./assets/img/dashboards/business-travel.svg",
      hoverIcon: "./assets/img/dashboards/business-travel-hover.svg",
      path: "/Business-travel-dashboard",
    },
    {
      id: "employee-commute",
      label: "Employee Commute",
      icon: "./assets/img/dashboards/commute.svg",
      hoverIcon: "./assets/img/dashboards/commute-hover.svg",
      path: "/Employee-commute-dashboard",
    },
    {
      id: "transport",
      label: "Transportation & Distribution",
      icon: "./assets/img/dashboards/transport.svg",
      hoverIcon: "./assets/img/dashboards/transport-hover.svg",
      path: "/transportation-Distribution-Dashboard",
    },
  ];

  const socialItems = [
    {
      id: "diversity",
      label: "Diversity",
      icon: "./assets/img/dashboards/diversity.svg",
      hoverIcon: "./assets/img/dashboards/diversity-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "health-safety",
      label: "Health & Safety",
      icon: "./assets/img/dashboards/health-safety.svg",
      hoverIcon: "./assets/img/dashboards/health-safety-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "training",
      label: "Training",
      icon: "./assets/img/dashboards/training.svg",
      hoverIcon: "./assets/img/dashboards/training-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
  ];

  const governanceItems = [
    {
      id: "board-composition",
      label: "Board Composition and Diversity",
      icon: "./assets/img/dashboards/board-composition.svg",
      hoverIcon: "./assets/img/dashboards/board-composition-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "customer-metrics",
      label: "Customer and Consumer Metrics",
      icon: "./assets/img/dashboards/customer-metrics.svg",
      hoverIcon: "./assets/img/dashboards/customer-metrics-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "anti-corruption",
      label: "Anti-Corruption and Anti-Bribery Efforts",
      icon: "./assets/img/dashboards/anti-corruption.svg",
      hoverIcon: "./assets/img/dashboards/anti-corruption-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
    {
      id: "supply-governance",
      label: "Supply Chain Governance",
      icon: "./assets/img/dashboards/supply-governance.svg",
      hoverIcon: "./assets/img/dashboards/supply-governance-hover.svg",
      path: "/comingsoon?source=dashboard",
    },
  ];

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd scf esg-dashboard">
          <div className="flex justify-content-between">
            <div className="steps__containt-hader">
              <h1 className="title">Dashboards</h1>
              <div role="presentation">
                {/* <Breadcrumbs
                  separator={<NavigateNextIcon fontSize="small" />}
                  aria-label="breadcrumb"
                >
                  <Typography color="text.primary">Forms & Apps</Typography>
                </Breadcrumbs> */}
              </div>
            </div>
          </div>
          <div className="scf">
            <div className="tabs">
              <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel
                  header={
                    <div className="tab-header">
                      <img src={activeIndex === 0 ? "./assets/img/dashboards/environment.svg" : "./assets/img/dashboards/env-hover.svg"} alt="Environment icon" className="tab-icon" />
                      <span className="tab-title">Environment</span>
                    </div>
                  }
                >
                  <div className="tab-content row gx-5">
                    <div className="cta-list col-md-6">
                      {/* Scope 1 Emissions */}
                      {environmentItems
                        .filter((item) => item.id === "emissions")
                        .map((item) => (
                          <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                            <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                            <span>{item.label}</span>
                            <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                          </div>
                        ))}

                      {/* Purchased Electricity */}
                      {environmentItems
                        .filter((item) => item.id === "electricity")
                        .map((item) => (
                          <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                            <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                            <span>{item.label}</span>
                            <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                          </div>
                        ))}

                      {/* Travel and Logistics with Nested Items */}
                      <div className="cta-item nested">
                        <div className="travel" onMouseEnter={() => setHoveredItem("logistics")} onMouseLeave={() => setHoveredItem(null)} onClick={() => (hasFeatureKey("dashboard.environment.travel-logistics") ? navigate("/Travel-Logistic-Dashboard") : explainPlanRefusal({ error: "feature_not_enabled", required: "dashboard.environment.travel-logistics" }))} style={hasFeatureKey("dashboard.environment.travel-logistics") ? undefined : { opacity: 0.45, cursor: "not-allowed" }}>
                          <img src={hoveredItem === "logistics" ? "./assets/img/dashboards/logistics-hover.png" : "./assets/img/dashboards/logistics.svg"} alt="Travel and Logistics" />
                          <span>Travel and Logistics</span>
                          <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-travel-arrow" />
                        </div>

                        <div className="nested-ctas">
                          {nestedItems.map((nested) => (
                            <div key={nested.id} className="cta-nest-item" onMouseEnter={() => setHoveredItem(nested.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => navigate(nested.path)}>
                              <img src={hoveredItem === nested.id ? nested.hoverIcon : nested.icon} alt={nested.label} />
                              <span>{nested.label}</span>
                              <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-nest-arrow" />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="cta-list col-md-6">
                      {/* Waste */}
                      {environmentItems
                        .filter((item) => item.id === "waste")
                        .map((item) => (
                          <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                            <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                            <span>{item.label}</span>
                            <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                          </div>
                        ))}

                      {/* Water */}
                      {environmentItems
                        .filter((item) => item.id === "water")
                        .map((item) => (
                          <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                            <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                            <span>{item.label}</span>
                            <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                          </div>
                        ))}

                      {/* Biodiversity */}
                      {environmentItems
                        .filter((item) => item.id === "biodiversity")
                        .map((item) => (
                          <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                            <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                            <span>{item.label}</span>
                            <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                          </div>
                        ))}
                    </div>
                  </div>
                </TabPanel>

                <TabPanel
                  header={
                    <div className="tab-header">
                      <img src={activeIndex === 1 ? "./assets/img/dashboards/social.svg" : "./assets/img/dashboards/soc-hover.svg"} alt="Social icon" className="tab-icon" />
                      <span className="tab-title">Social</span>
                    </div>
                  }
                >
                  <div className="tab-content row">
                    <div className="cta-list col-md-6">
                      {socialItems.map((item) => (
                        <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                          <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                          <span>{item.label}</span>
                          <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabPanel>
                <TabPanel
                  header={
                    <div className="tab-header">
                      <img src={activeIndex === 2 ? "./assets/img/dashboards/governance.svg" : "./assets/img/dashboards/gov-hover.svg"} alt="Governance icon" className="tab-icon" />
                      <span className="tab-title">Governance</span>
                    </div>
                  }
                >
                  <div className="tab-content row">
                    <div className="cta-list col-md-6">
                      {governanceItems.map((item) => (
                        <div key={item.id} className="cta-item" onMouseEnter={() => setHoveredItem(item.id)} onMouseLeave={() => setHoveredItem(null)} onClick={() => openTile(item.id, item.path)} style={tileStyle(item.id)}>
                          <img src={hoveredItem === item.id ? item.hoverIcon : item.icon} alt={item.label} />
                          <span>{item.label}</span>
                          <img src="./assets/img/dashboards/arrow.svg" alt="Go" className="cta-arrow" />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabPanel>
              </TabView>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ESGDashboard;
